import { useEffect, useRef } from 'react';

/**
 * Fires `onIntersect` when the returned ref's element scrolls into view.
 * Used to drive infinite-scroll page loads without an extra dependency
 * (frontend rule: implement the observer ourselves, keep installs minimal).
 *
 * `enabled` gates the observer (e.g. off while a fetch is in flight or no more
 * pages remain) so we don't fire repeatedly for the same sentinel.
 */
export function useIntersection(
  onIntersect: () => void,
  enabled: boolean,
) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Keep the latest callback without re-creating the observer each render.
  const cb = useRef(onIntersect);
  cb.current = onIntersect;

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) cb.current();
      },
      // Prefetch slightly before the sentinel is fully visible.
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled]);

  return ref;
}
